/* eslint-disable max-lines */
import config from "appConfig";
import moment from "moment";
import { getImageUrl } from "./asset";
import { getBaseDomain } from "./domain";
import { AdStatus, LocalStorageKey, MobileOS, PageRoute, PermutiveEvents, PriceType, PriceTypeValues, RentPriceTypeValues, ScreenSize } from "../models/enums";
import { UserI } from "../typings/interfaces";
import { VideoService } from "../models/enums"
import { Utils } from "../services/api";
import { PassedAdI, PermutiveDataI } from "../typings/routesTypes";
import { localStorage } from ".";
import { mapCategoryToHr } from "../routes/LanguageMapper";

// Roman-numeral tokens users might type interchangeably with Arabic digits
// in model / trim searches (e.g. "Golf 7" ↔ "Golf VII"). Order matters —
// longer tokens first so the regex doesn't greedily match "V" inside "VII".
// Word boundaries keep us from touching letters embedded in real words
// ("Sirion" isn't rewritten because "i" sits between other letters).
const ROMAN_TO_ARABIC = [
  { r: /\bviii\b/gi, a: "8" },
  { r: /\bxiii\b/gi, a: "13" },
  { r: /\bvii\b/gi, a: "7" },
  { r: /\biii\b/gi, a: "3" },
  { r: /\bxii\b/gi, a: "12" },
  { r: /\bvi\b/gi, a: "6" },
  { r: /\biv\b/gi, a: "4" },
  { r: /\bix\b/gi, a: "9" },
  { r: /\bxi\b/gi, a: "11" },
  { r: /\bii\b/gi, a: "2" },
  { r: /\bv\b/gi, a: "5" },
  { r: /\bx\b/gi, a: "10" },
  { r: /\bi\b/gi, a: "1" }
];

// Replaces standalone Roman-numeral tokens (I..XIII) with their Arabic
// equivalents so filter searches match either form ("Golf 7" ↔ "Golf VII").
export const normalizeRomanNumerals = (text: string): string => {
  if (!text) return "";
  let result = text;
  ROMAN_TO_ARABIC.forEach(({ r, a }) => {
    result = result.replace(r, a);
  });
  return result;
};

// Case-insensitive substring match with a Roman↔Arabic normalization
// fallback. Consumed by the filter-panel dropdowns (Make, Model, generic
// single/multi selects). Empty input matches everything (mirrors AntD's
// default filterOption).
//
// Two passes, in order:
//   1. Raw substring against the raw option text.
//   2. Raw substring against the OPTION with Roman numerals converted to
//      Arabic.
// Crucially the input is never normalized. Normalizing the input would
// rewrite lone letters ("V", "I") that the user is mid-typing as the
// start of the next word ("Variant") into digits and break the search:
//   * "Golf V v" (typing "Variant") — raw pass 1 finds it in "Golf V Variant".
//   * "Golf 6 V" (arabic 6 + typing "Variant") — pass 1 fails because
//     the option stores "VI"; pass 2 rewrites the option to "Golf 6
//     Variant" so the raw "Golf 6 V" substring matches.
//   * "Golf I" — raw pass 1 finds "Golf II / III / IV" via plain substring.
//   * "Golf 7" — pass 1 fails; pass 2 rewrites "Golf VII" to "Golf 7".
export const matchesSearchText = (input: string, text: string): boolean => {
  if (!input) return true;
  if (!text) return false;
  const inputLower = input.toLowerCase();
  const textLower = text.toLowerCase();
  if (textLower.indexOf(inputLower) >= 0) return true;
  const normalizedText = normalizeRomanNumerals(textLower);
  return normalizedText.indexOf(inputLower) >= 0;
};

export const formatNumber = (number: number) => {
  return number !== null && number !== undefined
    ? number.toLocaleString(config.localeCode, { maximumFractionDigits: 2 })
    : null;
};

export const generateUUID = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0; // eslint-disable-line no-bitwise
    const v = c === "x" ? r : (r & 0x3) | 0x8; // eslint-disable-line no-bitwise

    return v.toString(16);
  });

const defaultScrollOffset = 50;
const defaultScrollOptions = { behavior: "smooth" };

// smooth scroll to element with given id
export const scrollToElement = (
  id: string,
  offset = defaultScrollOffset,
  options = defaultScrollOptions
) => {
  const element = document.getElementById(id);
  const bodyRect = document.body.getBoundingClientRect().top;
  const elementRect = element.getBoundingClientRect().top;
  const elementPosition = elementRect - bodyRect;
  const maxScrollOffset =
    document.documentElement.scrollHeight -
    document.documentElement.clientHeight -
    1;
  const offsetPosition = Math.min(elementPosition - offset, maxScrollOffset);

  if (isIE() || isEdge() || isSafari()) {
    smoothScrollForOldBrowsers(offsetPosition);
  } else {
    // @ts-ignore
    window.scrollTo({
      ...options,
      top: offsetPosition
    });
  }
};

const smoothScrollForOldBrowsers = (offsetPosition: number) => {
  let i = window.pageYOffset;
  let sign = 1;
  if (i > offsetPosition) {
    sign = -1;
  }
  const int = setInterval(() => {
    window.scrollTo(0, i);
    i += 20 * sign;
    if (i * sign >= offsetPosition * sign) clearInterval(int);
  }, 20);
};


export const getMobileOS = () => {
  if (navigator.userAgent.toLowerCase().includes(MobileOS.android)) {
    return MobileOS.android;
  }

  if (navigator.userAgent.toLowerCase().includes(MobileOS.iOS)) {
    return MobileOS.iOS;
  }

  return "";
}

export const formatDate = (
  date: string,
  format = config.dateFormat,
  ignoreTimezone = false
) => {
  if (ignoreTimezone) {
    return moment.parseZone(date).format(format);
  }
  return moment(date).format(format);
};

export const checkEmptyObject = (object: object) =>
  !object || Object.keys(object).length === 0 ? undefined : object;

export const checkEmptyArray = (array: any) =>
  !array || array.length === 0 ? undefined : array;

export const isAllowedValue = (value: string | number | boolean) =>
  value || value === 0 || value === false;

export const parseFormControlValue = (fields: any, values: any) => {
  const result = {};

  fields.forEach((field: any, index: number) => {
    const value = values[index];
    if (isAllowedValue(value)) {
      if (value instanceof Array) {
        if (value.length > 0) {
          result[field] = value;
        }
      } else {
        result[field] = value;
      }
    }
  });

  return checkEmptyObject(result);
};

export const getStatusString = (value: number) => {
  switch (value) {
    case AdStatus.active:
      return "Aktivan";
    case AdStatus.paused:
      return "Pauziran";
    case AdStatus.sold:
      return "Prodano";
    case AdStatus.deleted:
      return "Obrisano";
    case AdStatus.blocked:
      return "Blokiran";
    case AdStatus.expired:
      return "Istekao";
    default:
      return "";
  }
};


export const getWindowWidth = () =>
  window.innerWidth ||
  document.documentElement.clientWidth ||
  document.getElementsByTagName("body")[0].clientWidth;

export const getWindowHeight = () =>
  window.innerHeight ||
  document.documentElement.clientHeight ||
  document.getElementsByTagName("body")[0].clientHeight;

/**
 * This method joins all elements of an array into a string with optional separators.
 * @param {Array} values The source data which will be processed.
 * @param {string} [separator=,] Specifies a string to separate elements of the array.
 * @param {string} [lastSeparator=and] Specifies a string to separate only for the last element of the array.
 * @return {string} A string with all array elements joined. If source array is empty, the empty string is returned.
 */
export const joinWithTwoSeparators = (
  values: any,
  separator = ",",
  lastSeparator = "and"
) => {
  if (!values || values.length === 0) {
    return "";
  }

  return values.reduce(
    (previousValue: any, currentValue: any, currentIndex: number) => {
      let preValue = previousValue;

      // if (currentIndex === values.length - 1) {
      // preValue += ` ${lastSeparator} `;
      // } else if (currentIndex !== values.length) {
      preValue += `${separator} `;
      // }

      return preValue + currentValue;
    }
  );
};

/**
 * This method is used for calculating the total number of pages to be displayed
 * in the ant design pagination component. That way we remove the possibility for the user
 * to go to the last page. This method should always be used when implementing pagination
 * because of elastic search default return number of ads (10.000).
 * @param adCount the total count of items received from the API
 * @param currentPage the current page the user is on
 * @param maxPages the max number of pages to be displayed on single screen
 * @returns
 */
export const calculateTotalPagingCount = (
  totalCount: number,
  currentPage: number,
  maxPages: number = 9
) => {
  // The current number of items which will be displayed based on the current page the user is on
  var currentItemCount = currentPage * config.maxAdPerPage;
  // The maximum number of pages to be displayed on each screen (10)
  var maxNumberOfPages = maxPages * config.maxAdPerPage;

  // If the total number of items is greater than the current count return the number current max page
  if (totalCount >= currentItemCount) {
    if (currentItemCount + maxNumberOfPages < totalCount) {
      return currentItemCount + maxNumberOfPages;
    }
  }
  // if current item count is greater than total count return the total count (last page)
  return totalCount;
};

export const replaceLineBreaksByTag = (text: string) =>
  text && text.replace(/(?:\r\n|\r|\n)/g, "<br>");

export const formatTag = (tagFormatted: any, text: string) =>
  tagFormatted.replace(config.tagToken, text);

export const removeProperties = (object: object, ...fieldNames: any) => {
  const result = {};
  const fieldNamesSet = new Set(fieldNames);

  isObject(object) &&
    Object.keys(object).forEach(key => {
      if (!fieldNamesSet.has(key)) {
        result[key] = object[key];
      }
    });

  return result;
};

export const getCurrentTime = () => new Date();

export const getUrlParams = (locationSearch: any, param?: string, parse: boolean = true) => {
  if (locationSearch) {
    const params = new URLSearchParams(locationSearch);
    const json = param ? params.get(param) : locationSearch.slice(1);

    if (!json) {
      return null;
    }

    if (!parse) {
      return json;
    }

    try {
      return JSON.parse(decodeURIComponent(json));
    } catch (error) {
      return null;
    }
  }

  return null;
};

export const getRecentSearchQuery = (category: string) => {
  const recentSearchQuery = localStorage.getItem(LocalStorageKey.recentSearchQuery) || {};
  return recentSearchQuery[mapCategoryToHr(category)] || {};
};

export const setRecentSearchQuery = (category: string, query: any, controlFields: object) => {
  if (!checkEmptyObject(controlFields)) return;
  const recentSearchQuery = localStorage.getItem(LocalStorageKey.recentSearchQuery) || {};
  const recentSearches = localStorage.getItem(LocalStorageKey.recentSearches) || [];

  // sanitize query object, delete anything from query object thats not in Object.keys(controlFields) array
  for (const key in query) {
    //preserve category and module
    if (["category", "module", "text"].includes(key)) continue;

    if (!Object.keys(controlFields).includes(key)) {
      delete query[key];
    }
  }

  query.text && localStorage.setItem(LocalStorageKey.recentSearches, recentSearches.map((item: any) => item.text === query.text ? {
    // Preserve existing module/category if not in query
    module: item.module,
    category: item.category,
    ...query,
    time: Date.now()
  } : item));
  recentSearchQuery[mapCategoryToHr(category)] = { ...query, text: undefined };
  localStorage.setItem(LocalStorageKey.recentSearchQuery, recentSearchQuery);
};

export const buildBaseUrl = (path: string) =>
  `${window.location.protocol}//${getBaseDomain()}${config.baseFolder}${path}`;

export const isObject = (value: any) => {
  const type = typeof value;
  return value !== null && type === "object";
};

export const parseUserDisplayName = (user: UserI | null) =>
  user.firstName !== null && user.lastName !== null
    ? `${user.firstName} ${user.lastName}`
    : user.username;

export const isSafari = () =>
  navigator.userAgent.toLowerCase().indexOf("safari/") > -1 &&
  navigator.userAgent.toLowerCase().indexOf("chrome") < 0;

export const isIE = () => {
  const ua = window.navigator.userAgent;

  // IE 10
  // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';

  // IE 11
  // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';

  const msie = ua.indexOf("MSIE ");
  if (msie > 0) {
    // IE 10 or older => return version number
    return parseInt(ua.substring(msie + 5, ua.indexOf(".", msie)), 10);
  }

  const trident = ua.indexOf("Trident/");
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf("rv:");

    return parseInt(ua.substring(rv + 3, ua.indexOf(".", rv)), 10);
  }

  // other browser
  return false;
};

export const isEdge = () => {
  const ua = window.navigator.userAgent;

  // Edge 12 (Spartan)
  // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';

  // Edge 13
  // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

  const edge = ua.indexOf("Edge/");
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    return parseInt(ua.substring(edge + 5, ua.indexOf(".", edge)), 10);
  }

  // other browser
  return false;
};

export interface parsedVideoEmbed {
  url: string,
  thumbnail: string,
  service: string,
}

export const parseVideoEmbedSrc = async (url: string): Promise<parsedVideoEmbed> => {
  if (!url) {
    return null;
  }

  // Default is Youtube RegExp and link
  let service = VideoService.youtube;
  let regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|\&v=)([^#\&\?]*).*/; // eslint-disable-line
  let embedUrl = "https://www.youtube.com/embed/";
  let thumbnail: string;
  let queryString = "?enablejsapi=1&version=3";

  if (url.includes("vimeo")) {
    service = VideoService.vimeo;
    regExp = /^.*vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|)(\d+)(?:|\/\?)/; // eslint-disable-line
    embedUrl = "https://player.vimeo.com/video/";
    queryString = "?api=1";
  }

  const match = url.match(regExp);
  const videoId = match && match[2];

  if (service === VideoService.youtube) {
    thumbnail = `https://img.youtube.com/vi/${videoId}/0.jpg`;
  }

  if (service === VideoService.vimeo) {
    let response = await Utils.getVimeoVideoThumbnail(videoId, null);
    if (response && response[0] && response[0].thumbnail_large) {
      thumbnail = response[0].thumbnail_large;
    } else {
      thumbnail = getImageUrl("vimeo-layer.jpg");
    }
  }
  return {
    service,
    url: `${embedUrl}${videoId}${queryString}`,
    thumbnail
  };
};

export const parsePriceString = (
  price: number,
) => `${formatNumber(price)} ${config.currencySymbol}`;

export const displayPrice = (
  price: number,
  priceTo: number,
  additionalTypes: any = {}
) => {
  const priceString = parsePriceString(price);
  const priceToString = parsePriceString(priceTo);
  const { priceType, rentPriceType } = additionalTypes;

  if (priceType) { // per m2 or total
    return `${priceString}${priceType === PriceType.PerM2 ? "/" : ""}${PriceTypeValues[priceType]}`;
  }

  if (rentPriceType) { // per day or per month
    return `${priceString}/${RentPriceTypeValues[rentPriceType]}`;
  }

  if (priceTo) {
    return `${priceString} - ${priceToString}`;
  }

  return priceString ? priceString : 0;
};

/**
 * Decimal adjustment of a number.
 * Reference from: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round$revision/1383484#Decimal_rounding
 */
// eslint-disable-next-line complexity
export const fixDecimal = (oldValue: any, oldExp: any) => {
  let value = oldValue;
  let exp = oldExp;
  // If the exp is undefined or zero...
  if (typeof exp === "undefined" || +exp === 0) {
    return Math.floor(value);
  }
  value = +oldValue;
  exp = +exp;
  // If the value is not a number or the exp is not an integer...
  if (
    value === null ||
    Number.isNaN(value) ||
    !(typeof exp === "number" && exp % 1 === 0)
  ) {
    return NaN;
  }
  // Shift
  value = value.toString().split("e");
  value = Math.floor(+`${value[0]}e${value[1] ? +value[1] - exp : -exp}`);
  // Shift back
  value = value.toString().split("e");
  return +`${value[0]}e${value[1] ? +value[1] + exp : exp}`;
};

export const convertBlobToBase64 = (blob: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsDataURL(blob);
  });

export const createDefaultValueObject = (fields: any) => {
  const value = {};

  fields.forEach((f: any) => {
    value[f] = null;
  });

  return value;
};

export const getSearchAdCardId = (adId: any) => `search-ad-card-${adId}`;

const getCurrentUrl = () =>
  `${window.location.pathname}${window.location.search}`;

const decodePath = (path: string) => {
  try {
    return decodeURI(path);
  } catch (e) {
    return path;
  }
};

// Remember which ad was opened from the search results, so we can scroll back
// to it when the user returns with the browser back button. The url is stored
// alongside the id so a stale value can never move a different result list.
export const setLastClickedSearchAd = (adId: any, adPath: string) => {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.setItem(LocalStorageKey.lastClickedSearchAd, {
    id: adId,
    url: getCurrentUrl(),
    adPath,
    hasOpenedAd: false
  });
};

// Called on every history change. The stored ad survives only the exact
// sequence "results -> ad -> back to results"; any other navigation (e.g. the
// header logo, a link on the ad page) drops it.
export const trackLastClickedSearchAdNavigation = (location: any, action: string) => {
  const stored = localStorage.getItem(LocalStorageKey.lastClickedSearchAd);
  if (!stored || !stored.id || action === "REPLACE") {
    return;
  }

  if (!stored.hasOpenedAd) {
    // The click must have actually opened the ad in this tab (not ctrl/middle click)
    if (action === "PUSH" && decodePath(location.pathname) === decodePath(stored.adPath)) {
      localStorage.setItem(LocalStorageKey.lastClickedSearchAd, { ...stored, hasOpenedAd: true });
    } else {
      localStorage.removeItem(LocalStorageKey.lastClickedSearchAd);
    }
    return;
  }

  // Browser back from the ad to the results: keep it for the search page to consume
  if (action === "POP" && getCurrentUrl() === stored.url) {
    return;
  }

  localStorage.removeItem(LocalStorageKey.lastClickedSearchAd);
};

// One-shot read: the value is always cleared, and only returned when the user
// came back from the ad to the exact result list that is on screen now.
export const takeLastClickedSearchAd = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const stored = localStorage.getItem(LocalStorageKey.lastClickedSearchAd);
  if (!stored || !stored.id) {
    return null;
  }

  localStorage.removeItem(LocalStorageKey.lastClickedSearchAd);

  return stored.hasOpenedAd && stored.url === getCurrentUrl() ? stored.id : null;
};

export const scrollToTop = () => window.scrollTo(0, 0);
export const scrollToBottom = () => window.scrollTo(0, document.body.scrollHeight);

export const removeThousandSeparator = (
  value: string,
  thousandSeparator: string
) => String(value).replace(new RegExp(`\\${thousandSeparator}`, "g"), "");

export const isMediumScreen = (width: number) => width < ScreenSize.extraLarge;

export const isSearchMobile = (width: number) => width <= ScreenSize.medium;

export const isMobileScreen = (width: number) =>
  width < ScreenSize.small && width > 0;

export const isDetailSwitch = (width: number) =>
  width <= ScreenSize.detailPageSwitchLayout && width > 0;

export const isLargerScreen = (width: number) =>
  width >= ScreenSize.detailPageSwitchLayout;

export const stringifyURI = (object: object, param: string, preserve: boolean = false) => {
  const stringifiedObject = window.encodeURIComponent(JSON.stringify(object));
  const currentParams = new URLSearchParams(preserve ? window.location.search : undefined);
  currentParams.set(param, stringifiedObject);
  // Remove search tracking params - they should not persist through filter/page changes
  currentParams.delete("search_type");
  currentParams.delete("search_query");
  return `?${currentParams.toString()}`;
}

export const showDropdownSearch = (optionsCount: number) => optionsCount >= 12;

export const isObjectAllNull = (object: object) =>
  !object ||
  Object.keys(object).length === 0 ||
  Object.keys(object).every(key => !object[key]);

export const resolveLastLineItem = ({ length }, index: number) => {
  if (length % 2 === 0) {
    // if the number of values in the list is even
    return index === length - 2 || index === length - 1; // last line item will be the last 2 parts of the list
  }

  // if the number of values in the list is odd then last line item will be the end of the list
  return index === length - 1;
};

export const dispatchPermutiveEvent = (eventType: string, user: UserI | Object, ad: PassedAdI) => {
  // @ts-ignore
  window.triggerPermutiveEvent = window.triggerPermutiveEvent || function () { };

  const shouldTriggerPermutiveEvent = JSON.stringify({
    ...ad.permutiveData,
    eventType,
  }) !== JSON.stringify(
    PermutiveEventData.get(eventType, ad.code)
  );

  if (shouldTriggerPermutiveEvent) {

    // @ts-ignore
    window.triggerPermutiveEvent(
      user, {
      ...ad.permutiveData,
      eventType,
    });

    PermutiveEventData.set(eventType, ad.code, {
      ...ad.permutiveData,
      eventType,
    });
  }
}

// @ts-ignore
window.permutiveEventData = {
  [PermutiveEvents.AdFavorite]: [],
  [PermutiveEvents.AdPreview]: []
};

export const PermutiveEventData = {
  // @ts-ignore
  get: (eventType: string, code: number) => window.permutiveEventData[eventType][code],
  // @ts-ignore
  set: (eventType: string, code: number, eventData: PermutiveDataI) => window.permutiveEventData[eventType][code] = eventData,
  // @ts-ignore
  clear: (eventType: string, code: number) => delete window.permutiveEventData[eventType][code],
}

// Domain extensions we treat as real links when written without http(s):// or www.
export const popularDomainExtensions = [
  '.com', '.net', '.org', '.info', '.biz', '.io', '.co', '.me',
  '.hr', '.rs', '.ba', '.si', '.de', '.at', '.it', '.fr', '.uk', '.us',
  '.eu', '.shop', '.store', '.site', '.online', '.web', '.app', '.dev',
  '.xyz', '.top', '.ru', '.cn', '.in', '.pl', '.cz', '.sk', '.hu',
];

// URL regex pattern for detecting links in messages
// Matches: https://..., http://..., www...., domain.tld/..., or a bare domain.tld (group 2)
const URL_REGEX = /(https?:\/\/[^\s<]+[^\s<.,;:!?'"\]\)>]|www\.[^\s<]+[^\s<.,;:!?'"\]\)>]|[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}\/[^\s<]*[^\s<.,;:!?'"\]\)>]|([a-zA-Z0-9][-a-zA-Z0-9]*(?:\.[a-zA-Z0-9][-a-zA-Z0-9]*)*\.[a-zA-Z]{2,}(?![-a-zA-Z0-9])(?:\/[^\s<]*[^\s<.,;:!?'"\]\)>])?))/gi;

// A bare domain is only a link if it has a known extension and isn't part of
// an email address or a longer word (e.g. "ivan@gmail.com", "file_name.com")
const isBareDomainLink = (content: string, url: string, index: number): boolean => {
  if (index > 0 && /[@\w.-]/.test(content[index - 1])) {
    return false;
  }

  const hostname = url.split('/')[0].toLowerCase();
  return popularDomainExtensions.includes(hostname.substring(hostname.lastIndexOf('.')));
};

// Allowed domains for clickable links
const ALLOWED_DOMAINS = ['index.hr'];

export interface ParsedMessagePart {
  type: 'text' | 'link';
  content: string;
  isExternal?: boolean;
}

export interface ParsedMessageResult {
  parts: ParsedMessagePart[];
  hasExternalLinks: boolean;
  hasLinks: boolean;
}

/**
 * Normalizes URL by adding https:// if missing
 */
export const normalizeUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return 'https://' + url;
};

/**
 * Checks if a URL belongs to an allowed domain (index.hr)
 */
export const isAllowedDomain = (url: string): boolean => {
  try {
    const normalizedUrl = normalizeUrl(url);
    const urlObj = new URL(normalizedUrl);
    const hostname = urlObj.hostname.toLowerCase();
    return ALLOWED_DOMAINS.some(function (domain) {
      return hostname === domain || hostname === 'www.' + domain || hostname.endsWith('.' + domain);
    });
  } catch (e) {
    return false;
  }
};

/**
 * Parses message content and extracts URLs, marking them as internal or external
 */
export const parseMessageContent = (content: string): ParsedMessageResult => {
  if (!content) {
    return { parts: [], hasExternalLinks: false, hasLinks: false };
  }

  const parts: ParsedMessagePart[] = [];
  let hasExternalLinks = false;
  let hasLinks = false;
  let lastIndex = 0;

  // Reset regex
  URL_REGEX.lastIndex = 0;

  let match;
  while ((match = URL_REGEX.exec(content)) !== null) {
    // Bare domains that don't qualify stay in the surrounding text
    if (match[2] && !isBareDomainLink(content, match[0], match.index)) {
      continue;
    }

    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex, match.index)
      });
    }

    const url = match[0];
    const isExternal = !isAllowedDomain(url);

    hasLinks = true;
    if (isExternal) {
      hasExternalLinks = true;
    }

    parts.push({
      type: 'link',
      content: url,
      isExternal
    });

    lastIndex = match.index + url.length;
  }

  // Add remaining text after last URL
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      content: content.substring(lastIndex)
    });
  }

  // If no URLs found, return whole content as text
  if (parts.length === 0) {
    parts.push({
      type: 'text',
      content: content
    });
  }

  return { parts, hasExternalLinks, hasLinks };
};

/**
 * Returns the correct Croatian plural form based on count.
 * Croatian has 3 plural forms:
 * - 1 (and numbers ending in 1, except 11): singular
 * - 2-4 (and numbers ending in 2-4, except 12-14): paucal
 * - 0, 5-20, and numbers ending in 0, 5-9, 11-14: genitive plural
 */
export const getCroatianPluralForm = (
  count: number,
  singular: string,
  paucal: string,
  genitivePlural: string
): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  // Teen numbers (11-14) are exceptions - use genitive plural
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return genitivePlural;
  }

  // Numbers ending in 1 (except 11)
  if (lastDigit === 1) {
    return singular;
  }

  // Numbers ending in 2, 3, 4 (except 12, 13, 14)
  if (lastDigit >= 2 && lastDigit <= 4) {
    return paucal;
  }

  // Everything else (0, 5-9)
  return genitivePlural;
};


export const hasBasicProfileData = (user: UserI): boolean => {
  var isCroatianAccount = user.countryId === "1d0ed9f8-c5b3-47f8-8ca1-7feb948d5aef";
  var hasRequiredIdentity = (isCroatianAccount && user.isIdentityNumberRequired) ? !!user.identityNumber : true;
  var hasDominantCategory = user.legalEntity ? (user.dominantCategories && user.dominantCategories.length > 0) : true;

  return isCroatianAccount
    ? !!(user.firstName && user.lastName && user.email && user.cityId && user.settlementId && user.street && user.zipCode && hasRequiredIdentity && hasDominantCategory)
    : !!(user.firstName && user.lastName && user.email && user.street && user.zipCode && hasDominantCategory);
};


export const getAdUrl = (ad) => `/${ad.module}/${ad.category}${PageRoute.details}/${ad.link}`;


// WEBPACK FOOTER //
// ./src/services/utils.ts